import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@cfleet/auth';
import {
  ArrowRight,
  Lock,
  Mail,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  UserPlus,
} from 'lucide-react';

/**
 * SignIn Component
 *
 * Branded sign-in page for CFleet Dispatcher.
 * Integrates with @cfleet/auth for authentication.
 *
 * Features:
 * - Email/password login via useAuth().login()
 * - Shows success message when redirected from signup
 * - Pre-fills email when coming from signup
 * - Links to forgot password and signup pages
 */

interface LocationState {
  from?: { pathname: string };
  signupSuccess?: boolean;
  email?: string;
  inviteAccepted?: boolean;
}

const SignIn: React.FC = () => {
  const { login, error, isLoading, clearError, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract state from location
  const state = location.state as LocationState | null;
  const from = state?.from?.pathname || '/';

  // Handle success messages and pre-fill email from signup
  useEffect(() => {
    if (state?.signupSuccess) {
      setSuccessMessage('Account created successfully! You can now sign in.');
      if (state.email) {
        setEmail(state.email);
      }
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    } else if (state?.inviteAccepted) {
      setSuccessMessage('Invitation accepted! You can now sign in.');
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccessMessage(null);

    try {
      await login({ email, password });
      // Navigation will happen via the useEffect when isAuthenticated becomes true
    } catch {
      // Error is handled by AuthProvider and available via error state
    }
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/forgot-password');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-sans text-slate-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-cfleet-100/50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-cfleet-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-cfleet-500/30">
                C
              </div>
              <span className="font-bold text-3xl text-slate-800 tracking-tight">cfleet</span>
            </div>
          </div>

          <div className="mb-8 text-center">
            <h2 className="text-xl font-bold text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-2">Sign in to manage your fleet operations</p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
              <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-700">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
              <AlertCircle size={18} className="text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-rose-700">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-bold text-slate-700 uppercase mb-1.5 tracking-wide"
              >
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                  <Mail size={18} />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="name@company.com"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label
                  htmlFor="password"
                  className="block text-xs font-bold text-slate-700 uppercase tracking-wide"
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-xs text-cfleet-600 hover:text-cfleet-700 font-bold hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cfleet-500 transition-colors">
                  <Lock size={18} />
                </div>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cfleet-500/20 focus:border-cfleet-500 transition-all placeholder:text-slate-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-cfleet-600 text-white py-3 rounded-lg text-sm font-bold shadow-lg shadow-cfleet-500/30 hover:bg-cfleet-700 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 mt-6 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-lg"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign In <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Invitation Button - for users joining via invitation */}
          <div className="mt-6 pt-6 border-t border-slate-100">
            <Link
              to="/accept-invite"
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 text-xs font-bold hover:bg-cfleet-50 hover:text-cfleet-600 hover:border-cfleet-200 transition-all"
            >
              <UserPlus size={14} /> Joining via invitation?
            </Link>
          </div>
        </div>

        <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 text-center">
          <p className="text-xs text-slate-500 font-medium">
            Don't have an account?{' '}
            <Link to="/signup" className="text-cfleet-600 font-bold hover:underline">
              Start 14-day free trial
            </Link>
          </p>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-slate-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default SignIn;
